// UserStatisticsPage.js — User-facing collection statistics

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import {
  Box, Typography, Grid, Paper, CircularProgress, Card, CardContent,
  LinearProgress, Divider, Container
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line
} from 'recharts';
import AppBarHeader from './AppBarHeader';
import axiosInstance from '../axiosInstance';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import ReputationPanel from './reputation/ReputationPanel';

const TYPE_LABELS = {
  games: 'Games', consoles: 'Consoles', lego: 'LEGO',
  movies: 'Movies', music: 'Music', books: 'Books', boardGames: 'Board Games'
};

const TYPE_COLORS = {
  games: '#1976d2', consoles: '#388e3c', lego: '#f57c00',
  movies: '#7b1fa2', music: '#c62828', books: '#00838f', boardGames: '#5d4037'
};

const RATING_COLORS = ['#ef5350', '#ff9800', '#ffc107', '#66bb6a', '#26a69a'];

const fmt = (n) => `£${parseFloat(n || 0).toFixed(2)}`;

// Summary card — a single stat with title + big number
function StatCard({ title, value, sub, color }) {
  return (
    <Card elevation={2}>
      <CardContent sx={{ pb: '16px !important' }}>
        <Typography variant="body2" color="text.secondary">{title}</Typography>
        <Typography variant="h4" sx={{ fontWeight: 700, color: color || 'text.primary', lineHeight: 1.2, mt: 0.5 }}>
          {value}
        </Typography>
        {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
      </CardContent>
    </Card>
  );
}

// Horizontal completion bar
function CompletionBar({ label, done, total, color }) {
  if (!total) return null;
  const pct = Math.round((done / total) * 100);
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2">{label}</Typography>
        <Typography variant="body2" color="text.secondary">{done} / {total} ({pct}%)</Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{ height: 8, borderRadius: 4, bgcolor: 'grey.200',
          '& .MuiLinearProgress-bar': { bgcolor: color || 'primary.main', borderRadius: 4 } }}
      />
    </Box>
  );
}

export default function UserStatisticsPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const user = token ? jwtDecode(token) : null;

  const { isFeatureEnabled } = useFeatureFlags();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [socialStats, setSocialStats] = useState(null);
  const [socialLoading, setSocialLoading] = useState(true);
  const [reviewStats, setReviewStats] = useState(null);

  useEffect(() => {
    axiosInstance.get('/stats')
      .then(r => { setData(r.data); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });

    axiosInstance.get('/follow/social-stats')
      .then(res => setSocialStats(res.data))
      .catch(() => setSocialStats(null))
      .finally(() => setSocialLoading(false));

    axiosInstance.get(`/reviews/user/${user?.id}?limit=1`)
      .then(res => setReviewStats({ reviewCount: res.data.total }))
      .catch(() => setReviewStats(null));
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  if (loading) {
    return (
      <>
        <AppBarHeader isAdmin={user?.isAdmin} onLogout={handleLogout} />
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (error || !data) {
    return (
      <>
        <AppBarHeader isAdmin={user?.isAdmin} onLogout={handleLogout} />
        <Box sx={{ p: 4 }}>
          <Typography color="error">Failed to load statistics: {error}</Typography>
        </Box>
      </>
    );
  }

  const { collections, wishlists, totals, allRatings, monthlyGrowth } = data;

  // Pie chart — collection type breakdown (active items only)
  const pieData = Object.entries(collections)
    .map(([type, c]) => ({ name: TYPE_LABELS[type], value: c.total - c.sold, fill: TYPE_COLORS[type] }))
    .filter(d => d.value > 0);

  // Bar chart — ratings distribution (1–5 stars across all types)
  const ratingsData = [1, 2, 3, 4, 5].map(star => ({
    name: '★'.repeat(star),
    count: allRatings[star] || 0
  }));

  // Wishlist bar chart
  const wishlistData = Object.entries(wishlists)
    .filter(([, v]) => v > 0)
    .map(([type, count]) => ({ name: TYPE_LABELS[type], count, fill: TYPE_COLORS[type] }));

  // Top platforms (games)
  const topPlatforms = Object.entries(collections.games.platforms || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([name, count]) => ({ name, count }));

  // Top LEGO themes
  const topThemes = Object.entries(collections.lego.themes || {})
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([name, count]) => ({ name, count }));

  return (
    <>
      <AppBarHeader isAdmin={user?.isAdmin} onLogout={handleLogout} />
      <Container maxWidth="lg" sx={{ py: 4 }}>

        <Typography variant="h4" fontWeight={700} gutterBottom>My Collection Stats</Typography>

        {/* ── Top summary cards ── */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard title="Total Items" value={totals.items} />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard title="Wishlist Items" value={totals.wishlistItems} />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard title="Items Sold" value={totals.sold} color="warning.main" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard title="Sold Revenue" value={fmt(totals.soldRevenue)} color="success.main" />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard title="Rated Items" value={totals.ratedItems} />
          </Grid>
          <Grid item xs={6} sm={4} md={2}>
            <StatCard title="LEGO Pieces" value={collections.lego.totalPieces?.toLocaleString() || 0} />
          </Grid>
        </Grid>

        {/* ── Per-collection totals + wishlist ── */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {Object.entries(collections).map(([type, c]) => (
            <Grid item xs={6} sm={4} md={2} key={type}>
              <StatCard
                title={TYPE_LABELS[type]}
                value={c.total - c.sold}
                sub={c.sold > 0 ? `+${c.sold} sold` : `${wishlists[type]} wishlisted`}
                color={TYPE_COLORS[type]}
              />
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={3}>

          {/* ── Collection breakdown pie ── */}
          <Grid item xs={12} md={5}>
            <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
              <Typography variant="h6" gutterBottom>Collection Breakdown</Typography>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                    outerRadius={90} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}>
                    {pieData.map((entry, i) => (
                      <Cell key={i} fill={entry.fill} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* ── Ratings distribution ── */}
          <Grid item xs={12} md={7}>
            <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
              <Typography variant="h6" gutterBottom>Ratings Distribution</Typography>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={ratingsData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar dataKey="count" name="Items">
                    {ratingsData.map((_, i) => <Cell key={i} fill={RATING_COLORS[i]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* ── Monthly growth ── */}
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Monthly Additions (last 12 months)</Typography>
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={monthlyGrowth} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Legend />
                  {Object.keys(TYPE_LABELS).map(type => (
                    <Line key={type} type="monotone" dataKey={type} name={TYPE_LABELS[type]}
                      stroke={TYPE_COLORS[type]} dot={false} strokeWidth={2} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* ── Completion rates ── */}
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Completion</Typography>
              <CompletionBar label="Games played" done={collections.games.played} total={collections.games.total - collections.games.sold} color={TYPE_COLORS.games} />
              <CompletionBar label="Games completed" done={collections.games.completed} total={collections.games.total - collections.games.sold} color="#42a5f5" />
              <CompletionBar label="Movies watched" done={collections.movies.watched} total={collections.movies.total - collections.movies.sold} color={TYPE_COLORS.movies} />
              <CompletionBar label="Music listened" done={collections.music.listened} total={collections.music.total - collections.music.sold} color={TYPE_COLORS.music} />
              <CompletionBar label="Books read" done={collections.books.read} total={collections.books.total - collections.books.sold} color={TYPE_COLORS.books} />
              {collections.boardGames && <CompletionBar label="Board games played" done={collections.boardGames.played} total={collections.boardGames.total - collections.boardGames.sold} color={TYPE_COLORS.boardGames} />}
            </Paper>
          </Grid>

          {/* ── Backlog ── */}
          {['games','lego','movies','music','books','boardGames'].some(t => collections[t]?.inBacklog > 0) && (
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Backlog</Typography>
                <CompletionBar label="Games" done={collections.games.inBacklog} total={collections.games.total - collections.games.sold} color={TYPE_COLORS.games} />
                <CompletionBar label="LEGO" done={collections.lego.inBacklog} total={collections.lego.total - collections.lego.sold} color={TYPE_COLORS.lego} />
                <CompletionBar label="Movies" done={collections.movies.inBacklog} total={collections.movies.total - collections.movies.sold} color={TYPE_COLORS.movies} />
                <CompletionBar label="Music" done={collections.music.inBacklog} total={collections.music.total - collections.music.sold} color={TYPE_COLORS.music} />
                <CompletionBar label="Books" done={collections.books.inBacklog} total={collections.books.total - collections.books.sold} color={TYPE_COLORS.books} />
                {collections.boardGames && <CompletionBar label="Board Games" done={collections.boardGames.inBacklog} total={collections.boardGames.total - collections.boardGames.sold} color={TYPE_COLORS.boardGames} />}
              </Paper>
            </Grid>
          )}

          {/* ── Did Not Finish ── */}
          {['games','lego','movies','music','books','boardGames'].some(t => collections[t]?.isDNF > 0) && (
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Did Not Finish (DNF)</Typography>
                {[
                  { key: 'games',      label: 'Games' },
                  { key: 'lego',       label: 'LEGO' },
                  { key: 'movies',     label: 'Movies' },
                  { key: 'music',      label: 'Music' },
                  { key: 'books',      label: 'Books' },
                  { key: 'boardGames', label: 'Board Games' },
                ]
                  .filter(({ key }) => collections[key]?.isDNF > 0)
                  .map(({ key, label }) => {
                    const c = collections[key];
                    return (
                      <Box key={key} sx={{ mb: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2">{label}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {c.isDNF} DNF'd
                            {c.avgDnfPercent != null ? ` · avg ${c.avgDnfPercent}% complete` : ''}
                          </Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(100, Math.round((c.isDNF / Math.max(1, c.total - c.sold)) * 100))}
                          sx={{ height: 8, borderRadius: 4, bgcolor: 'grey.200',
                            '& .MuiLinearProgress-bar': { bgcolor: 'warning.main', borderRadius: 4 } }}
                        />
                      </Box>
                    );
                  })}
              </Paper>
            </Grid>
          )}

          {/* ── Wishlist breakdown ── */}
          {wishlistData.length > 0 && (
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                <Typography variant="h6" gutterBottom>Wishlist</Typography>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={wishlistData} layout="vertical" margin={{ top: 0, right: 16, left: 40, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" allowDecimals={false} />
                    <YAxis type="category" dataKey="name" width={60} />
                    <Tooltip />
                    <Bar dataKey="count" name="Items">
                      {wishlistData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          )}

          {/* ── Top game platforms ── */}
          {topPlatforms.length > 0 && (
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Top Game Platforms</Typography>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={topPlatforms} layout="vertical" margin={{ top: 0, right: 16, left: 80, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" allowDecimals={false} />
                    <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill={TYPE_COLORS.games} name="Games" />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          )}

          {/* ── Top LEGO themes ── */}
          {topThemes.length > 0 && (
            <Grid item xs={12} md={6}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Top LEGO Themes</Typography>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={topThemes} layout="vertical" margin={{ top: 0, right: 16, left: 80, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" allowDecimals={false} />
                    <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill={TYPE_COLORS.lego} name="Sets" />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
          )}

          {/* ── Sold items summary ── */}
          {totals.sold > 0 && (
            <Grid item xs={12}>
              <Paper elevation={2} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Sold Items Summary</Typography>
                <Grid container spacing={2}>
                  {Object.entries(collections)
                    .filter(([, c]) => c.sold > 0)
                    .map(([type, c]) => (
                      <Grid item xs={6} sm={4} md={2} key={type}>
                        <Box>
                          <Typography variant="body2" color="text.secondary">{TYPE_LABELS[type]}</Typography>
                          <Typography variant="h5" fontWeight={700} sx={{ color: TYPE_COLORS[type] }}>{c.sold}</Typography>
                          {c.soldRevenue > 0 && (
                            <Typography variant="caption" color="success.main">{fmt(c.soldRevenue)}</Typography>
                          )}
                        </Box>
                      </Grid>
                    ))}
                </Grid>
              </Paper>
            </Grid>
          )}

          {/* ── Reputation panel ── */}
          {isFeatureEnabled('enableReputationSystem') && user?.id && (
            <Grid item xs={12}>
              <ReputationPanel userId={user.id} />
            </Grid>
          )}

          {/* ── Social stats card ── */}
          {isFeatureEnabled('enableFollowSystem') && (
            <Grid item xs={12}>
              <Card elevation={2}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Social</Typography>
                  {socialLoading ? (
                    <CircularProgress size={20} />
                  ) : socialStats ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" fontWeight={700}>{socialStats.followerCount}</Typography>
                        <Typography variant="caption" color="text.secondary">Followers</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" fontWeight={700}>{socialStats.followingCount}</Typography>
                        <Typography variant="caption" color="text.secondary">Following</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" fontWeight={700}>
                          {socialStats.activityPostCount ?? '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Activity Posts (90d)</Typography>
                      </Box>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h4" fontWeight={700}>
                          {socialStats.activityLikesReceived ?? '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">Likes Received (90d)</Typography>
                      </Box>
                      {isFeatureEnabled('enableReviews') && reviewStats && (
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" fontWeight={700}>{reviewStats.reviewCount}</Typography>
                          <Typography variant="caption" color="text.secondary">Reviews Written</Typography>
                        </Box>
                      )}
                      {socialStats.isPrivate && (
                        <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
                          Activity stats are hidden because your feed is set to private.
                        </Typography>
                      )}
                    </Box>
                  ) : null}
                </CardContent>
              </Card>
            </Grid>
          )}

        </Grid>
      </Container>
    </>
  );
}
